// SMPTE タイムコード計算用の純粋関数群。Vue に依存しない。
// Videojs.vue（Composition API）と Clip*Component.vue（Options API）の両方から共用する。

const TIMECODE_PATTERN = /^(\d{2}):(\d{2}):(\d{2})([:;])(\d{2})$/;

function pad2(value) {
  return String(value).padStart(2, '0');
}

/**
 * "HH:MM:SS:FF" または "HH:MM:SS;FF" を分解する。不正な形式は null を返す。
 */
export function parseTimecode(tc) {
  if (typeof tc !== 'string') {
    return null;
  }
  const matched = TIMECODE_PATTERN.exec(tc);
  if (!matched) {
    return null;
  }
  const [, hh, mm, ss, separator, ff] = matched;
  return {
    hh: Number(hh),
    mm: Number(mm),
    ss: Number(ss),
    ff: Number(ff),
    dropFrame: separator === ';',
  };
}

/**
 * タイムコードを通算フレーム番号に変換する。
 * @param {string} tc "HH:MM:SS:FF" または "HH:MM:SS;FF"
 * @param {number} nominalFps 名目フレームレート（29.97fps なら 30）
 */
export function timecodeToFrames(tc, nominalFps) {
  const parsed = parseTimecode(tc);
  if (!parsed) {
    return null;
  }
  const { hh, mm, ss, ff, dropFrame } = parsed;
  const totalMinutes = hh * 60 + mm;
  const ndfFrames = (hh * 3600 + mm * 60 + ss) * nominalFps + ff;

  if (!dropFrame) {
    return ndfFrames;
  }

  const dropCount = Math.round(nominalFps * 0.066666);
  return ndfFrames - dropCount * (totalMinutes - Math.floor(totalMinutes / 10));
}

/**
 * 通算フレーム番号をタイムコード文字列に変換する（SMPTE ドロップフレーム標準アルゴリズム）。
 * @param {number} frameNumber
 * @param {number} nominalFps 名目フレームレート（29.97fps なら 30）
 * @param {boolean} dropFrame
 */
export function framesToTimecode(frameNumber, nominalFps, dropFrame) {
  let frame = Math.round(frameNumber);

  if (!dropFrame) {
    const framesPer24h = nominalFps * 3600 * 24;
    frame = ((frame % framesPer24h) + framesPer24h) % framesPer24h;

    const ff = frame % nominalFps;
    const ss = Math.floor(frame / nominalFps) % 60;
    const mm = Math.floor(frame / (nominalFps * 60)) % 60;
    const hh = Math.floor(frame / (nominalFps * 3600)) % 24;

    return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}:${pad2(ff)}`;
  }

  const dropCount = Math.round(nominalFps * 0.066666);
  const framesPer10Min = nominalFps * 600 - dropCount * 9;
  const framesPerMin = nominalFps * 60 - dropCount;
  const framesPer24h = framesPer10Min * 144;

  frame = ((frame % framesPer24h) + framesPer24h) % framesPer24h;

  const d = Math.floor(frame / framesPer10Min);
  const m = frame % framesPer10Min;

  if (m > dropCount) {
    frame += dropCount * 9 * d + dropCount * Math.floor((m - dropCount) / framesPerMin);
  } else {
    frame += dropCount * 9 * d;
  }

  const ff = frame % nominalFps;
  const ss = Math.floor(frame / nominalFps) % 60;
  const mm = Math.floor(frame / (nominalFps * 60)) % 60;
  const hh = Math.floor(frame / (nominalFps * 3600)) % 24;

  return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)};${pad2(ff)}`;
}

/**
 * 開始タイムコード・フレームレート・経過秒数から、現在のタイムコード文字列を計算する。
 * @param {string} startTimecode "HH:MM:SS:FF" または "HH:MM:SS;FF"
 * @param {string|number} frameRate DB由来のフレームレート文字列/数値（"25","30","29.97"等）
 * @param {number} offsetSeconds 開始TCからの経過秒数
 * @returns {string|null} 不正な入力の場合は null
 */
export function computeCurrentTimecode(startTimecode, frameRate, offsetSeconds) {
  const parsed = parseTimecode(startTimecode);
  if (!parsed) {
    return null;
  }

  const actualFps = parseFloat(frameRate);
  if (!Number.isFinite(actualFps) || actualFps <= 0) {
    return null;
  }
  const nominalFps = Math.round(actualFps);

  const startFrames = timecodeToFrames(startTimecode, nominalFps);
  if (startFrames === null) {
    return null;
  }

  const offsetFrames = Math.round((Number(offsetSeconds) || 0) * actualFps);

  return framesToTimecode(startFrames + offsetFrames, nominalFps, parsed.dropFrame);
}
